import type {CSSProperties} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import WaveSurfer from 'wavesurfer.js';
import {PMW_COLORS_NEUTRAL} from '@Utils/color.util';
import {ThreeDotsLoader} from '@Components/three-dots-loader';
import {DOTS_COLOR} from '@Components/three-dots-loader/three-dots-loader';
import type {WaveSurferParams} from 'wavesurfer.js/types/params';
import {noop} from '@Utils/general.util';
import styles from './audio-waveform.module.scss';

interface AudioWaveformProps {
  id: string;
  url: string;
  isPlaying: boolean;
  endTime: number;
  seek: number;
  additionalClasses?: string;
  waveSurferProps?: Partial<WaveSurferParams>;
  onPlay?: Function;
  onReady?: Function;
  onLoading?: Function;
  onFinish?: Function;
  onProgress?: Function;
  onPause?: Function;
  duration: number; // wavesurfer.getDuration() returns duration that doesn't match with ours
  doShowLoader?: boolean;
  style?: CSSProperties;
  pxPerSec?: number;
}

const defaultWaveSurferProps: WaveSurferParams = {
  waveColor: PMW_COLORS_NEUTRAL.NEUTRAL_18,
  progressColor: PMW_COLORS_NEUTRAL.NEUTRAL_28,
  cursorColor: '#fff',
  barWidth: 4,
  barRadius: 3,
  cursorWidth: 1,
  interact: false,
  height: 32,
  barGap: 4,
  fillParent: true,
  container: '',
};

export function AudioWaveform({
  onPlay = noop,
  doShowLoader = true,
  onReady = noop,
  onLoading = noop,
  onPause = noop,
  onFinish = noop,
  onProgress = noop,
  ...props
}: AudioWaveformProps) {
  const waveSurferContainerRef = useRef<HTMLDivElement>(null);
  // CodeReviewAhmedLater: Is there any packages that provides the types for wavesurfer? Use it here so
  // that es lint error could be solved
  const wavesurfer: any = useRef(null);
  const [loading, setLoading] = useState(true);
  const [isReady, setIsReady] = useState(false);

  const onPlayingStatusChanged = () => {
    if (props.isPlaying && typeof props.seek !== 'undefined' && typeof props.endTime !== 'undefined') {
      wavesurfer.current.play(props.seek, props.endTime);
    } else {
      wavesurfer.current.pause();
    }
  };

  const onEndTimeChanged = () => {
    wavesurfer.current.on('pause', onPause);

    return () => {
      wavesurfer.current.un('pause', onPause);
    };
  };

  useEffect(() => {
    const waveSurferProps = {
      ...defaultWaveSurferProps,
      ...props.waveSurferProps,
      container: document.querySelector(`#${props.id}`),
      ...(props.pxPerSec !== undefined ? {minPxPerSec: props.pxPerSec} : {}),
    } as WaveSurferParams;

    wavesurfer.current = WaveSurfer.create(waveSurferProps);

    wavesurfer.current.on('ready', () => {
      setLoading(false);
      setIsReady(true);
      onReady();
    });

    wavesurfer.current.on('loading', onLoading);
    wavesurfer.current.on('play', onPlay);
    wavesurfer.current.on('pause', onPause);
    wavesurfer.current.on('finish', onFinish);
    wavesurfer.current.on('audioprocess', onProgress);
    wavesurfer.current.load(props.url);

    return (): void => {
      wavesurfer.current.destroy();
    };
  }, []);

  useEffect(onPlayingStatusChanged, [props.isPlaying]);

  useEffect(onEndTimeChanged, [props.endTime]);

  useEffect(() => {
    if (isReady && wavesurfer.current && props.pxPerSec !== undefined) {
      wavesurfer.current.zoom(props.pxPerSec);
    }
  }, [props.pxPerSec, isReady]);

  return (
    <div style={props.style} ref={waveSurferContainerRef} className={`${props.additionalClasses} ${styles.wavesurferContainer} radius-4 spacing-p-1`} id={props.id}>
      {loading && doShowLoader ? <ThreeDotsLoader distance={6} size={6} color={DOTS_COLOR.DARK} extraClasses={`${styles.loadingContainer}`} /> : null}
    </div>
  );
}
